import { message } from "antd";
import {
  useCreateUserMutation,
  useUpdateUserMutation,
  useToggleUserStatusMutation,
  useUpdateUserOverridesMutation,
} from "../../Services";

interface UseUserMutationProps {
  isCreating: boolean;
  editingUser: any;
  form: any;
  closeDrawer: () => void;
  overrides?: any[];
}

export const useUserMutation = ({
  isCreating,
  editingUser,
  form,
  closeDrawer,
  overrides,
}: UseUserMutationProps) => {
  const createUserMutation = useCreateUserMutation();
  const updateUserMutation = useUpdateUserMutation();
  const toggleUserStatusMutation = useToggleUserStatusMutation();
  const updateUserOverridesMutation = useUpdateUserOverridesMutation();

  const handleSuccess = (isCreate: boolean, userId?: any) => {
    if (userId && overrides && overrides.length > 0) {
      updateUserOverridesMutation.mutate({ userId, overrides });
    }
    message.success(
      isCreate ? "User created successfully!" : "User updated successfully!",
    );
    closeDrawer();
  };

  const handleFailure = (res: any) => {
    if (res?.errors) {
      Object.entries(res.errors).forEach(([field, msg]) => {
        form.setFields([
          {
            name: field,
            errors: [msg as string],
          },
        ]);
      });
    } else {
      message.error(res?.message || "Operation failed.");
    }
  };

  const submitMutation = {
    isPending: createUserMutation.isPending || updateUserMutation.isPending,
    mutate: async (values: any) => {
      if (isCreating) {
        createUserMutation.mutate(values, {
          onSuccess: (res) => {
            if (res?.success) {
              handleSuccess(true, res?.data?.id);
            } else {
              handleFailure(res);
            }
          },
          onError: (err: any) => {
            message.error(err?.message || "An unexpected error occurred.");
          },
        });
      } else {
        updateUserMutation.mutate(
          {
            id: editingUser!.id,
            ...values,
          },
          {
            onSuccess: (res) => {
              if (res?.success) {
                handleSuccess(false, editingUser!.id);
              } else {
                handleFailure(res);
              }
            },
            onError: (err: any) => {
              message.error(err?.message || "An unexpected error occurred.");
            },
          },
        );
      }
    },
  };

  const toggleStatusMutation = {
    isPending: toggleUserStatusMutation.isPending,
    mutate: ({
      id,
      newStatus,
    }: {
      id: number;
      newStatus: "Active" | "Inactive";
    }) => {
      toggleUserStatusMutation.mutate(
        { id, status: newStatus },
        {
          onSuccess: (res) => {
            if (res?.success) {
              message.success(`User marked as ${newStatus}.`);
            } else {
              message.error(res?.message || "Failed to change status.");
            }
          },
          onError: (err: any) => {
            message.error(err?.message || "Failed to change status.");
          },
        },
      );
    },
  };

  return {
    submitMutation,
    toggleStatusMutation,
  };
};
